import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { platform_person_api } from "../../DAL/Platform/Console";
import { C, KIND_LABEL } from "./consoleTheme";
import { BackBar, Panel, Field, Grid, Stat, StateScreen, AuditList } from "./ConsoleShared";

/**
 * One person, as the platform sees them.
 *
 * Nothing here is a copy of their own screen. Their screen cannot show payment
 * state, payout state, which regulator their row resolves to, or what XAir
 * staff have previously done to their account — and those are the fields that
 * actually explain the ticket. Logging in as them would show less, not more.
 */
export default function ConsolePerson() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const res = await platform_person_api(id);
        if (dead) return;
        if (res?.code === 200) setD(res); else setErr(res?.message || "Could not load");
      } catch (e) { if (!dead) setErr(e?.message || "Could not load"); }
    })();
    return () => { dead = true; };
  }, [id]);

  if (err) return <StateScreen><BackBar label="Console" /><p style={{ color: C.high }}>{err}</p></StateScreen>;
  if (!d) return <StateScreen><BackBar label="Console" />Loading…</StateScreen>;

  const { person, region, school, billing, learning, bookings, staffHistory } = d;

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "28px", color: C.text }}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <BackBar label="Console" />

        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>{person.name || person.email}</h1>
          <div style={{ color: C.textDim, fontSize: 13, marginTop: 4 }}>
            {KIND_LABEL[person.kind]}{school ? ` · ${school.name}` : ""}
            {person.active === false && <span style={{ color: C.high }}> · inactive</span>}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 16 }}>
          <Stat label="Topics attempted" value={learning.topicsAttempted} />
          <Stat label="Average score" value={learning.averageScore == null ? "—" : `${learning.averageScore}%`} />
          <Stat label="Bookings" value={bookings.total} />
          <Stat label="Staff actions" value={staffHistory.length} />
        </div>

        <Panel title="Identity">
          <Grid>
            <Field label="Email" value={person.email} mono />
            <Field label="Account id" value={person.id} mono />
            <Field label="Role" value={person.role} />
            <Field label="Joined" value={person.createdAt ? new Date(person.createdAt).toLocaleDateString() : null} />
          </Grid>
        </Panel>

        {/* Region as a derivation. "Why is this student seeing UK content" is
            the real question; a bare "UK" does not answer it. */}
        <Panel title="Content entitlement">
          <Grid>
            <Field label="Effective region" value={region.effective} />
            <Field label="Authority" value={region.authority} />
            <Field label="Licences visible" value={region.licenses.join(", ")} mono />
          </Grid>
          <div style={{
            marginTop: 8, padding: "9px 11px", borderRadius: 6,
            background: C.panelHi, fontSize: 13, color: C.textDim,
          }}>
            {region.explanation}
          </div>
        </Panel>

        <Panel title="Billing">
          <Grid>
            <Field label="Stripe customer" value={billing.stripeCustomerId} mono />
            <Field label="Stripe subscription" value={billing.stripeSubscriptionId} mono />
          </Grid>
        </Panel>

        <Panel title={`Bookings (${bookings.total})`}>
          {bookings.recent.length ? (
            <div>
              {bookings.recent.map((b, i) => (
                <div key={b.id} style={{
                  display: "flex", justifyContent: "space-between", gap: 12,
                  padding: "8px 0", borderTop: i ? `1px solid ${C.border}` : "none", fontSize: 13,
                }}>
                  <span>{b.subject || "—"}</span>
                  <span style={{ color: C.textDim, fontSize: 12 }}>
                    {b.startsAt ? new Date(b.startsAt).toLocaleDateString() : ""} · {b.status}
                    {/* Payment and payout shown together: a booking that looks
                        fine to the student is often the one whose payout never ran. */}
                    {b.payment ? ` · paid:${b.payment}` : ""}{b.payout ? ` · payout:${b.payout}` : ""}
                  </span>
                </div>
              ))}
            </div>
          ) : <p style={{ fontSize: 13, color: C.textFaint, margin: 0 }}>No bookings.</p>}
        </Panel>

        <Panel title="What XAir staff have done to this account">
          <AuditList rows={staffHistory} />
        </Panel>
      </div>
    </div>
  );
}
